<template>
<section :class="classes">
	<Heading :level="1" text="Business Details" />
	<UserMessage v-if="error" message-type="error" :message-text="error" />

	<form @submit.prevent="saveAndSubmit" novalidate ref="form">
		<InputField
			name="ua_businessname"
			ref="ua_businessname"
			label-text="Please enter your business name"
			:value="formData.ua_businessname"
			@emitFieldData="(val) => formData.ua_businessname = val"
			required-error="Please enter your business name"
			:validation-rules="[(v) => rules.containsLength(v, 3, null)]" />

		<SelectField
			name="ua_legalstatus"
			ref="ua_legalstatus"
			label-text="Please select the legal status of your business from the options below."
			:value="formData.ua_legalstatus"
			@emitFieldData="(val) => formData.ua_legalstatus = val"
			required-error="Please select an option"
			:options-loading="false"
			:options="legalStatusOpts" />

		<template v-if="isCompany">
			<CompaniesHouseSearch
				name="ua_companyregistrationnumber"
				ref="ua_companyregistrationnumber"
				label-text="Please enter your Companies House number, click on search, and select your business from the options displayed"
				:value="formData.ua_companyregistrationnumber"
				@input="formData.ua_companyregistrationnumber = $event.target.value"
				@companySelected="updateCompany"
				placeholder="Enter Companies House Number"
				required-error="Please enter your Companies House number" />

			<UserMessage v-if="!companySelected" class="m-t-1" message-type="error" message-text="Please search your companies house number and select a company from the list" />

			<DateTextField
				label-text="Please provide the incorporation date of your company"
				ref="ua_datebusinessstartedtrading"
				name="ua_datebusinessstartedtrading"
				:value="formData.ua_datebusinessstartedtrading"
				@emitFieldData="(val) => formData.ua_datebusinessstartedtrading = val"
				min-years-before-today="0"
				min-date="01/01/1870"
				:help-text="{text: 'Please enter in the following format: DD/MM/YYYY'}"
				required-error="Please enter a date, month and year"
				:validation-rules="[rules.isValidDate, rules.isMinYears, rules.afterMinDate]" />

			<template v-if="canHaveCharityStatus">
				<RadioGroupField
					name="ua_haveregisteredcharitystatus"
					ref="ua_haveregisteredcharitystatus"
					label-text="Does your organisation have Registered Charity Status?"
					:value="formData.ua_haveregisteredcharitystatus"
					@emitFieldData="((v) => formData.ua_haveregisteredcharitystatus = v)"
					:options="[{ label: 'Yes', value: 1 }, { label: 'No', value: 2 }]"
					required-error="Please choose an option" />

				<RadioGroupField
					name="ua_registeredonmutualsregister"
					ref="ua_registeredonmutualsregister"
					label-text="Registered on the Mutuals Register?"
					:value="formData.ua_registeredonmutualsregister"
					@emitFieldData="((v) => formData.ua_registeredonmutualsregister = v)"
					:options="[{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }]"
					required-error="Please choose an option" />

				<InputField
					v-if="isMutualsRegistered"
					name="ua_mutualsregisterregistrationnumber"
					ref="ua_mutualsregisterregistrationnumber"
					label-text="Mutuals Registration Number"
					:value="formData.ua_mutualsregisterregistrationnumber"
					@emitFieldData="(val) => formData.ua_mutualsregisterregistrationnumber = val"
					required-error="Please enter your registration number"
					:validation-rules="[(v) => rules.containsLength(v, 0, 20)]" />
			</template>

			<template v-if="isCharity">
				<InputField
					name="ua_charitynumber"
					ref="ua_charitynumber"
					label-text="Registered Charity Number"
					:value="formData.ua_charitynumber"
					@emitFieldData="(val) => formData.ua_charitynumber = val"
					required-error="Please enter your registered charity number"
					:validation-rules="[(v) => rules.containsLength(v, 6, 10)]" />

				<DateTextField
					label-text="Date you registered with the Charity Commission"
					ref="ua_dateregisteredwithcharitycommission"
					name="ua_dateregisteredwithcharitycommission"
					:value="formData.ua_dateregisteredwithcharitycommission"
					@emitFieldData="(val) => formData.ua_dateregisteredwithcharitycommission = val"
					min-years-before-today="0"
					min-date="01/01/1870"
					:help-text="{text: 'Please enter in the following format: DD/MM/YYYY'}"
					required-error="Please enter a date, month and year"
					:validation-rules="[rules.isValidDate, rules.isMinYears, rules.afterMinDate]" />
			</template>

			<InputField
				name="ua_siccode"
				ref="ua_siccode"
				label-text="Please provide the SIC code of your company"
				:value="formData.ua_siccode"
				@emitFieldData="(val) => formData.ua_siccode = val"
				required-error="Please enter your business name"
				:validation-rules="[(v) => rules.containsLength(v, 3, null)]" />

			<FormFieldSet legend="Registered address">
				<template #fields>
					<InputField
						name="ua_registeredaddress"
						ref="ua_registeredaddress"
						label-text="Registered Address line 1"
						:help-text="{text: 'Please include  the street name and number'}"
						:value="formData.ua_registeredaddress"
						@emitFieldData="(val) => formData.ua_registeredaddress = val"
						required-error="Please enter the first line of your trading address"
						:validation-rules="[(v) => rules.containsLength(v, 6)]" />

					<InputField
						name="ua_registeredaddressline2"
						ref="ua_registeredaddressline2"
						label-text="Registered Address line 2"
						:value="formData.ua_registeredaddressline2"
						@emitFieldData="(val) => formData.ua_registeredaddressline2 = val"
						:is-required="false" />

					<InputField
						name="ua_registeredaddresstowncity"
						ref="ua_registeredaddresstowncity"
						label-text="Registered Address Town/City"
						:value="formData.ua_registeredaddresstowncity"
						@emitFieldData="(val) => formData.ua_registeredaddresstowncity = val"
						required-error="Please enter the town/city of your trading address"
						:validation-rules="[(v) => rules.containsLength(v, 3)]" />

					<InputField
						name="ua_registeredpostcode"
						ref="ua_registeredpostcode"
						label-text="Registered Address Postcode"
						:help-text="{text: 'Please enter your postcode in a valid format e.g. DH1 1TW'}"
						placeholder="e.g. DH1 1TW"
						:value="formData.ua_registeredpostcode"
						@emitFieldData="(val) => formData.ua_registeredpostcode = val"
						required-error="Please enter your trading address postcode"
						:validation-rules="[rules.validatePostcode]" />
				</template>
			</FormFieldSet>

			<RadioGroupField
				name="ua_tradingaddresssameasregistered"
				ref="ua_tradingaddresssameasregistered"
				label-text="Is your trading address the same as the registered address above?"
				:value="formData.ua_tradingaddresssameasregistered"
				@emitFieldData="((v) => prefillAddress(v))"
				:options="[{ label: 'Yes', value: 1 }, { label: 'No', value: 2 }]"
				required-error="Please choose an option" />

			<FormFieldSet legend="Trading address">
				<template #fields>
					<InputField
						ref="ua_tradingaddress"
						name="ua_tradingaddress"
						label-text="Trading Address line 1"
						:value="formData.ua_tradingaddress"
						@emitFieldData="(val) => formData.ua_tradingaddress = val"
						required-error="Please provide your trading address"
						:is-disabled="tradingAddressPrefilled"
						:validation-rules="[(v) => rules.containsLength(v, 6)]" />

					<InputField
						ref="ua_tradingaddressline2"
						name="ua_tradingaddressline2"
						:is-disabled="tradingAddressPrefilled"
						label-text="Trading Address line 2"
						:value="formData.ua_tradingaddressline2"
						@emitFieldData="(val) => formData.ua_tradingaddressline2 = val"
						:is-required="false" />

					<InputField
						ref="ua_tradingaddresstowncity"
						name="ua_tradingaddresstowncity"
						label-text="Trading Address Town/City"
						:is-disabled="tradingAddressPrefilled"
						:value="formData.ua_tradingaddresstowncity"
						@emitFieldData="(val) => formData.ua_tradingaddresstowncity = val"
						required-error="Please provide your trading address town/city"
						:validation-rules="[(v) => rules.containsLength(v, 3)]" />

					<InputField
						ref="ua_tradingpostcode"
						name="ua_tradingpostcode"
						label-text="Trading Address Postcode"
						:help-text="{text: 'Please enter your postcode in a valid format e.g. DH1 1TW'}"
						placeholder="e.g. DH1 1TW"
						:is-disabled="tradingAddressPrefilled"
						:value="formData.ua_tradingpostcode"
						@emitFieldData="(val) => formData.ua_tradingpostcode = val"
						required-error="Please provide your trading address postcode"
						:validation-rules="[rules.validatePostcode]" />
				</template>
			</FormFieldSet>
		</template>

		<template v-if="isSoleTrader || isPartnership">
			<InputField
				name="ua_uniquetaxreferencenumber"
				ref="ua_uniquetaxreferencenumber"
				label-text="UTR Number"
				:help-text="{text: 'A UTR number (Unique Taxpayer Reference) is a ten digit number issued directly by HM Revenue & Customs.'}"
				:value="formData.ua_uniquetaxreferencenumber"
				@emitFieldData="(val) => formData.ua_uniquetaxreferencenumber = val"
				required-error="Please enter your UTR number"
				:validation-rules="[utrNum]" />

			<DateTextField
				label-text="Date you registered with HMRC"
				ref="ua_dateregisteredwithhmrc"
				name="ua_dateregisteredwithhmrc"
				:value="formData.ua_dateregisteredwithhmrc"
				@emitFieldData="(val) => formData.ua_dateregisteredwithhmrc = val"
				min-years-before-today="0"
				min-date="01/01/1870"
				:help-text="{text: 'Please enter in the following format: DD/MM/YYYY'}"
				required-error="Please enter a date, month and year"
				:validation-rules="[rules.isValidDate, rules.isMinYears, rules.afterMinDate]" />
		</template>

		<template v-if="isOther">
			<InputField
				name="ua_legalstatusother"
				ref="ua_legalstatusother"
				label-text="Please specify your legal status"
				:help-text="{text: 'If you selected other for Legal Status, please enter your business type here.'}"
				:value="formData.ua_legalstatusother"
				@emitFieldData="(val) => formData.ua_legalstatusother = val"
				required-error="Please enter your type of business"
				:validation-rules="[(v) => rules.containsLength(v, 6)]" />
		</template>

		<SelectField
			name="ua_sectorparentlookup"
			ref="ua_sectorparentlookup"
			label-text="Please select the parent sector of your business from the options below."
			:value="formData.ua_sectorparentlookup"
			@emitFieldData="((v) => filterSubSectors(v, true))"
			required-error="Please select an option"
			:options-loading="!getParentSectors.length"
			:options="getParentSectors" />

		<SelectField
			v-if="formData.ua_sectorparentlookup"
			name="ua_sectorlookup"
			ref="ua_sectorlookup"
			label-text="Please select the sub sector of your business from the options below."
			:value="formData.ua_sectorlookup"
			@emitFieldData="(val) => formData.ua_sectorlookup = val"
			required-error="Please select an option"
			:options-loading="sectorOptsLoading"
			:options="filteredSectorOpts" />

		<TextArea
			ref="ua_businessoverview"
			name="ua_businessoverview"
			:value="formData.ua_businessoverview"
			@emitFieldData="(val) => formData.ua_businessoverview = val"
			:min-chars="0"
			:max-chars="10000"
			label-text="Please describe the nature of your business in a sentence or two."
			:help-text="{text: 'Describe what products or services your business produces/offers and who your principal customers are.'}"
			required-error="Please provide a description"
			:validation-rules="[(v) => rules.containsLength(v, 50, 10000)]" />

		<TextArea
			ref="ua_briefdescriptionoforganisation"
			name="ua_briefdescriptionoforganisation"
			:value="formData.ua_briefdescriptionoforganisation"
			@emitFieldData="(val) => formData.ua_briefdescriptionoforganisation = val"
			:min-chars="0"
			:max-chars="5000"
			label-text="Please provide a brief history of your business to date."
			:help-text="{text: 'Please outline how long your business has been trading and how it has performed over recent years.  We are also keen to hear if you have any plans to diversify and if so the rationale behind this decision.'}"
			required-error="Please provide a brief history of your business"
			:validation-rules="[(v) => rules.containsLength(v, 25, 5000)]" />

		<NumberField
			name="ua_lastapprovedemployees"
			ref="ua_lastapprovedemployees"
			label-text="How many staff (full-time equivalent) does your business currently employ?"
			:help-text="{text: 'Please include yourself if you are named on the payroll and for each part time member of staff please enter them as 0.5. for example 1 full time equivalent and 1 part time equivalent would be entered as 1.5.'}"
			:step="0.5"
			:value="formData.ua_lastapprovedemployees"
			@emitFieldData="(val) => formData.ua_lastapprovedemployees = val"
			required-error="Please enter the number of employees"
			invalid-number="Please enter a number in increments of 0.5" />

		<RadioGroupField
			name="ua_isthebusinesspartofagroup"
			ref="ua_isthebusinesspartofagroup"
			label-text="Is your business part of a larger group?"
			:value="formData.ua_isthebusinesspartofagroup"
			@emitFieldData="(val) => formData.ua_isthebusinesspartofagroup = val"
			:options="[{ label: 'Yes', value: 1 }, { label: 'No', value: 2 }]"
			required-error="Please choose an option" />

		<template v-if="isPartOfGroup">
			<TextArea
				ref="ua_parentcompanydetails"
				name="ua_parentcompanydetails"
				:value="formData.ua_parentcompanydetails"
				@emitFieldData="(val) => formData.ua_parentcompanydetails = val"
				:min-chars="0"
				:max-chars="2000"
				label-text="Please provide the parent company details"
				:help-text="{text: 'Provide: Name of your parent company, address, company registration number'}"
				required-error="Please provide the parent company details"
				:validation-rules="[(v) => rules.containsLength(v, 0, 2000)]" />
		</template>

		<CheckboxField
			ref="ua_isyourbusinessvatregistered"
			name="ua_isyourbusinessvatregistered"
			label-text="Are you VAT Registered?"
			:true-value="1"
			:false-value="0"
			:value="formData.ua_isyourbusinessvatregistered"
			@emitFieldData="(val) => formData.ua_isyourbusinessvatregistered = val"
			:is-required="false"
			:options="[{ label: 'My business is VAT registered.', value: 'vat registered' }]" />

		<template v-if="isVATRegistered">
			<InputField
				name="ua_vatregistrationnumber"
				ref="ua_vatregistrationnumber"
				:help-text="{text: 'Your VAT number will usually start with GB followed by 9 digits e.g GB123456789. Please only enter the numbers in this field.'}"
				label-text="VAT Registration number"
				:value="formData.ua_vatregistrationnumber"
				@emitFieldData="(val) => formData.ua_vatregistrationnumber = val"
				required-error="Please enter your VAT Registration Number"
				mask="vat-mask"
				:validation-rules="[rules.vatNum]" />
		</template>
	</form>
</section>
</template>

<script>
import { mapGetters } from 'vuex';

import { formSteps } from '@/mixins/appFormSteps';
import { formValidator } from '@/mixins/formValidator';
import { validationRules } from '@/utilities/validationRules';

import CompaniesHouseSearch from '@/components/FormComponents/CompaniesHouseSearch';

export default {
	name: 'BusinessDetails',
	components: {
		CompaniesHouseSearch
	},
	mixins: [formValidator, formSteps],
	props: {
		classes: {
			type: String,
			default: ''
		}
	},
	data () {
		return {
			rules: validationRules,
			error: '',
			formData: {
				ua_businessname: '',
				ua_sectorparentlookup: '',
				ua_sectorlookup: '',
				ua_legalstatus: '',
				ua_companyregistrationnumber: '',
				ua_datebusinessstartedtrading: '',
				ua_registeredaddress: '',
				ua_registeredaddressline2: '',
				ua_registeredaddresstowncity: '',
				ua_registeredpostcode: '',
				ua_tradingaddresssameasregistered: 2,
				ua_tradingaddress: '',
				ua_tradingaddressline2: '',
				ua_tradingaddresstowncity: '',
				ua_tradingpostcode: '',
				ua_uniquetaxreferencenumber: '',
				ua_dateregisteredwithhmrc: '',
				ua_legalstatusother: '',
				ua_businessoverview: '',
				ua_briefdescriptionoforganisation: '',
				ua_lastapprovedemployees: '',
				ua_isthebusinesspartofagroup: 2,
				ua_parentcompanydetails: '',
				ua_haveregisteredcharitystatus: '',
				ua_registeredonmutualsregister: '',
				ua_mutualsregisterregistrationnumber: '',
				ua_charitynumber: '',
				ua_dateregisteredwithcharitycommission: '',
				ua_siccode: '',
				ua_isyourbusinessvatregistered: 0,
				ua_vatregistrationnumber: ''
			},
			legalStatusOpts: [
				{ label: 'Charitable Community Benefit Society', value: 108760004 },
				{ label: 'Charitable Incorporated Organisation (CIO) or Scottish Charitable Incorporated Organisation (SCIO)', value: 953880001 },
				{ label: 'Community Benefit Society', value: 953880002 },
				{ label: 'Community Interest Company (CIC) - Limited by guarantee', value: 108760007 },
				{ label: 'Community Interest Company (CIC) - Limited by shares', value: 953880003 },
				{ label: 'Company registration pending', value: 953880004 },
				{ label: 'Limited Liability Partnership (LLP)', value: 108760003 },
				{ label: 'Industrial & Provident Society', value: 953880005 },
				{ label: 'Partnership', value: 108760002 },
				{ label: 'Private Limited Company (Ltd) - Limited by guarantee', value: 953880006 },
				{ label: 'Private Limited Company (Ltd) - Limited by shares', value: 953880007 },
				{ label: 'Private unlimited Company', value: 953880008 },
				{ label: 'Public Limited Company', value: 108760000 },
				{ label: 'Sole Trader', value: 108760001 },
				{ label: 'Trust', value: 953880009 },
				{ label: 'Unincorporated association', value: 953880011 },
				{ label: 'Not for Profit Organisation', value: 108760005 },
				{ label: 'Other, please specify ....', value: 108760006 }
			],
			filteredSectorOpts: this.sectorOpts,
			sectorOptsLoading: true,
			companySelected: false
		};
	},
	computed: {
		isCompany () {
			const companyTypes = [108760007, 953880003, 108760003, 953880006, 953880007, 108760000];
			return companyTypes.includes(parseInt(this.formData.ua_legalstatus));
		},
		isCharity () {
			const charityTypes = [108760004, 953880001];
			return charityTypes.includes(parseInt(this.formData.ua_legalstatus)) || parseInt(this.formData.ua_haveregisteredcharitystatus, 10) === 1;
		},
		isSoleTrader () {
			return parseInt(this.formData.ua_legalstatus) === 108760001;
		},
		isPartnership () {
			return parseInt(this.formData.ua_legalstatus) === 108760002;
		},
		isOther () {
			return parseInt(this.formData.ua_legalstatus) === 108760006;
		},
		tradingAddressPrefilled () {
			return parseInt(this.formData.ua_tradingaddresssameasregistered) === 1;
		},
		isPartOfGroup () {
			return parseInt(this.formData.ua_isthebusinesspartofagroup) === 1;
		},
		canHaveCharityStatus () {
			const companyTypes = [108760004, 953880002, 953880005, 108760006];
			return companyTypes.includes(parseInt(this.formData.ua_typeofbusinessownership));
		},
		isVATRegistered () {
			return this.formData.ua_isyourbusinessvatregistered === 1;
		},
		...mapGetters('settings', ['getParentSectors', 'getSectors'])

	},
	mounted () {
		this.getParentfromSector();

		this.filterSubSectors();

		// Here we don't want to consider the company NOT selected until the local storage item exists.
		this.companySelected = window.localStorage.getItem('companySelected') !== 'false';
	},
	methods: {
		utrNum (value) {
			const pattern = /^([0-9]{10})$|^([0-9]{9}[kK]{1})$/;
			return pattern.test(String(value).toLowerCase()) || 'Please enter a valid UTR Number';
		},
		filterSubSectors (value = null, resetSector = false) {
			if (value) {
				this.formData.ua_sectorparentlookup = value;
			}

			if (resetSector) {
				this.formData.ua_sectorlookup = '';
			}

			let match = [];
			this.filteredSectorOpts = [];
			this.getSectors.forEach(item => {
				match = this.getSectors.filter(item => item.parentId === this.formData.ua_sectorparentlookup);
			});

			if (match.length) {
				match.forEach(matchItem => {
					this.filteredSectorOpts.push({
						label: matchItem.label,
						value: matchItem.value
					});
				});
			}

			this.sectorOptsLoading = false;
		},
		getParentfromSector () {
			let match = [];
			this.getSectors.forEach(item => {
				match = this.getSectors.filter(item => item.value === this.formData.ua_sectorlookup);
			});

			this.formData.ua_sectorparentlookup = match[0] ? match[0].parentId : '';
		},
		updateCompany (data) {
			this.companySelected = true;

			this.formData.ua_businessname = data.CompanyName;
			this.formData.ua_registeredaddress = data.AddressLine1;
			this.formData.ua_registeredaddressline2 = data.AddressLine2;
			this.formData.ua_registeredaddresstowncity = data.Locality;
			this.formData.ua_registeredpostcode = data.PostalCode;
			this.formData.ua_datebusinessstartedtrading = data.DateOfCreation;
			if (this.formData.ua_tradingaddresssameasregistered === 1) {
				this.formData.ua_tradingaddress = data.AddressLine1;
				this.formData.ua_tradingaddressline2 = data.AddressLine2;
				this.formData.ua_tradingaddresstowncity = data.Locality;
				this.formData.ua_tradingpostcode = data.PostalCode;
			}
		},
		prefillAddress (val) {
			this.formData.ua_tradingaddresssameasregistered = val;

			if (this.tradingAddressPrefilled) {
				this.formData.ua_tradingaddress = this.formData.ua_registeredaddress;
				this.formData.ua_tradingaddressline2 = this.formData.ua_registeredaddressline2;
				this.formData.ua_tradingaddresstowncity = this.formData.ua_registeredaddresstowncity;
				this.formData.ua_tradingpostcode = this.formData.ua_registeredpostcode;
			} else {
				this.formData.ua_tradingaddress = '';
				this.formData.ua_tradingaddressline2 = '';
				this.formData.ua_tradingaddresstowncity = '';
				this.formData.ua_tradingpostcode = '';
			}
		}
	}
};
</script>
